import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const normalizeValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    return normalized.endsWith('°') ? normalized.slice(0, -1) : normalized;
};

const formatRotationLabel = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
        return `${numeric}°`;
    }
    return String(value);
};

const RotationItem = ({ rotation, isChecked, onToggle }) => {
    const handleChange = (event) => {
        onToggle(event.target.checked);
    };

    return (
        <div className="flex items-center justify-between my-2">
            <div className="flex items-center">
                <input
                    checked={isChecked}
                    onChange={handleChange}
                    name={`rotation-${rotation.id}`}
                    id={`rotation-${rotation.id}`}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                />
                <label
                    htmlFor={`rotation-${rotation.id}`}
                    className="ml-2 flex items-center text-sm text-gray-600">
                    <span>{rotation.label}</span>
                    <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">
                        {rotation.count ?? 0}
                    </span>
                </label>
            </div>
        </div>
    );
};

RotationItem.propTypes = {
    rotation: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        count: PropTypes.number.isRequired,
    }).isRequired,
    isChecked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const RotationList = ({
    screens = [],
    allScreens = [],
    selectedRotations = [],
    availableRotations = [],
    availableRotationCounts = [],
    onChange,
    isFilterByAudiences = false,
}) => {
    const { t } = useTranslation();

    const baseScreens = useMemo(() => {
        const dataset = Array.isArray(allScreens) && allScreens.length
            ? allScreens
            : screens;
        if (!Array.isArray(dataset)) return [];
        if (!isFilterByAudiences) return dataset;
        return dataset.filter((screen) => screen?.hasAudience || screen?.audiences);
    }, [allScreens, screens, isFilterByAudiences]);

    const selectedNormalized = useMemo(
        () =>
            new Set(
                (selectedRotations || [])
                    .map((value) => normalizeValue(value))
                    .filter(Boolean)
            ),
        [selectedRotations]
    );

    const rotations = useMemo(() => {
        const map = new Map();
        const rotationCountLookup = new Map();
        (availableRotationCounts || []).forEach((item) => {
            const key = normalizeValue(item?.value);
            const count = Number(item?.count ?? 0);
            if (!key || !Number.isFinite(count)) return;
            rotationCountLookup.set(key, count);
        });
        const normalizedAvailableRotations = Array.isArray(availableRotations)
            ? availableRotations
                  .map((value) => normalizeValue(value))
                  .filter(Boolean)
            : [];

        if (normalizedAvailableRotations.length > 0) {
            normalizedAvailableRotations.forEach((value) => {
                if (!value || map.has(value)) return;
                map.set(value, {
                    id: value,
                    label: formatRotationLabel(value),
                    count: rotationCountLookup.get(value) ?? 0,
                });
            });
        } else {
            baseScreens.forEach((screen) => {
                const raw = screen?.rotation ?? screen?.screen_rotation;
                if (raw === null || raw === undefined || raw === '') return;

                const id = normalizeValue(raw);
                if (!id) return;

                    const label = formatRotationLabel(raw);
                    if (!map.has(id)) {
                        map.set(id, {
                            id,
                            label,
                            count: rotationCountLookup.get(id) ?? 1,
                        });
                    } else {
                        const existing = map.get(id);
                        existing.count += 1;
                    map.set(id, existing);
                }
            });
        }

        selectedNormalized.forEach((value) => {
            if (!value || map.has(value)) return;
            const matchedOriginal = (selectedRotations || []).find(
                (item) => normalizeValue(item) === value
            );
            const display = formatRotationLabel(matchedOriginal ?? value);
            map.set(value, {
                id: value,
                label: display,
                count: rotationCountLookup.get(value) ?? 0,
            });
        });

        return Array.from(map.values()).sort((a, b) => {
            const aHas = (a.count ?? 0) > 0 ? 0 : 1;
            const bHas = (b.count ?? 0) > 0 ? 0 : 1;
            if (aHas !== bHas) return aHas - bHas;
            return a.label.localeCompare(b.label);
        });
    }, [
        baseScreens,
        selectedRotations,
        selectedNormalized,
        availableRotations,
        availableRotationCounts,
    ]);

    const isSelected = (rotation) => selectedNormalized.has(rotation.id);

    const updateSelection = (rotation, checked) => {
        if (!onChange) return;
        if (checked) {
            if (isSelected(rotation)) return;
            onChange([...selectedRotations, rotation.id]);
        } else {
            onChange(
                selectedRotations.filter(
                    (item) => normalizeValue(item) !== rotation.id
                )
            );
        }
    };

    if (rotations.length === 0) return null;

    return (
        <div className="font-light pt-2 w-40">
            <div className="title font-light py-2 shadow-sm">
                {t(
                    'sections.campaigns.new-campaign.wizard-screens.filters.rotation-label'
                )}
            </div>
            <div className="max-h-60 overflow-y-auto overflow-hidden">
                {rotations.map((rotation) => (
                    <RotationItem
                        key={rotation.id}
                        rotation={rotation}
                        isChecked={isSelected(rotation)}
                        onToggle={(checked) =>
                            updateSelection(rotation, checked)
                        }
                    />
                ))}
            </div>
        </div>
    );
};

RotationList.propTypes = {
    screens: PropTypes.array,
    allScreens: PropTypes.array,
    selectedRotations: PropTypes.array,
    availableRotations: PropTypes.array,
    availableRotationCounts: PropTypes.array,
    onChange: PropTypes.func,
    isFilterByAudiences: PropTypes.bool,
};

export default RotationList;
